// Debug / admin panel. Toggled with Ctrl/Cmd+D from anywhere in the app.
// Holds the bearer key the worker checks against env.ADMIN_KEY for write
// endpoints. Stored in localStorage so the user only pastes it once per
// browser.

import { useEffect, useState } from "react";
import { COLORS, tStyles } from "../styles";
import {
  AdminRequiredError,
  api,
  clearAdminKey,
  fmtMillsUsd,
  getAdminKey,
  setAdminKey,
} from "../api";

interface Props {
  open: boolean;
  onClose: () => void;
  onBackfillDone?: () => void;
}

export function DebugPanel({ open, onClose, onBackfillDone }: Props) {
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setKey(getAdminKey() ?? "");
    setSavedAt(null);
  }, [open]);

  if (!open) return null;

  const onSave = () => {
    setAdminKey(key.trim());
    setSavedAt(Date.now());
  };

  const onForget = () => {
    clearAdminKey();
    setKey("");
    setSavedAt(Date.now());
  };

  return (
    <div style={tStyles.cmdBackdrop} onClick={onClose}>
      <div
        style={{ ...tStyles.cmdPanel, width: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={tStyles.cmdInputRow}>
          <span style={tStyles.cmdPrompt}>⚙</span>
          <span style={{ ...tStyles.cmdInput, flex: 1 }}>debug · admin key</span>
          <span style={tStyles.cmdHint}>ESC / Ctrl+D to close</span>
        </div>
        <div
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: 1.5 }}>
            Sent as <code>Authorization: Bearer …</code> on POST/PUT calls.
            Must match the worker's <code>ADMIN_KEY</code> env var. Stored in
            localStorage on this device only — never sent anywhere except
            this app's API.
          </div>
          <label
            style={{ color: COLORS.textDim, fontSize: 10, letterSpacing: 1 }}
          >
            ADMIN KEY
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type={show ? "text" : "password"}
              value={key}
              onChange={(e) => {
                setKey(e.target.value);
                setSavedAt(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSave();
              }}
              autoFocus
              spellCheck={false}
              autoComplete="off"
              placeholder="paste admin key, press enter"
              style={{
                flex: 1,
                background: COLORS.bg,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.text,
                padding: "6px 8px",
                fontFamily: "inherit",
                fontSize: 12,
              }}
            />
            <button
              onClick={() => setShow((s) => !s)}
              style={{
                ...tStyles.cmdLaunch,
                width: 60,
                justifyContent: "center",
              }}
              title="toggle visibility"
            >
              {show ? "hide" : "show"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onForget} style={tStyles.cmdLaunch}>
              forget
            </button>
            <button
              onClick={onSave}
              style={{ ...tStyles.drillButton, marginTop: 0 }}
            >
              save
            </button>
          </div>
          {savedAt && (
            <div
              style={{
                color: COLORS.up,
                fontSize: 10,
                letterSpacing: 1,
                textAlign: "right",
              }}
            >
              ✓ saved at {new Date(savedAt).toLocaleTimeString()}
            </div>
          )}
          <BackfillSection onDone={onBackfillDone} />
        </div>
      </div>
    </div>
  );
}

// 30d gap-fill backup. Use when the recurring 7d sync missed enough days that
// the gap has aged out of /counts/recent's window. Hits /counts/all behind the
// scenes, so this costs ~10× the 7d toolbar SYNC — wrapped in a click-to-confirm
// so it isn't fired accidentally.
function BackfillSection({ onDone }: { onDone?: () => void }) {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "confirm" }
    | { kind: "running" }
    | { kind: "done"; specs: number; calls: number; mills: number }
    | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const run = async () => {
    setState({ kind: "running" });
    try {
      const r = await api.cronRun(30);
      if (r.errors.length > 0) {
        setState({ kind: "error", msg: r.errors[0] ?? "unknown" });
        return;
      }
      setState({
        kind: "done",
        specs: r.specs,
        calls: r.api_calls,
        mills: r.cost_mills,
      });
      onDone?.();
    } catch (e) {
      const msg =
        e instanceof AdminRequiredError
          ? "admin key required — save one above first"
          : e instanceof Error
            ? e.message
            : String(e);
      setState({ kind: "error", msg });
    }
  };

  return (
    <div
      style={{
        marginTop: 8,
        paddingTop: 12,
        borderTop: `1px solid ${COLORS.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <label
        style={{ color: COLORS.textDim, fontSize: 10, letterSpacing: 1 }}
      >
        30D BACKFILL
      </label>
      <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: 1.5 }}>
        Backup for when the toolbar's <code>↻ SYNC 7d</code> can't reach the
        gap anymore (e.g. you forgot to refresh for more than a week). Fetches
        the last 30 days for every active spec via <code>/counts/all</code>{" "}
        (~$0.01 per call, ~4 calls per spec).
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {state.kind === "idle" && (
          <button
            onClick={() => setState({ kind: "confirm" })}
            style={tStyles.cmdLaunch}
          >
            backfill 30d…
          </button>
        )}
        {state.kind === "confirm" && (
          <>
            <button
              onClick={() => setState({ kind: "idle" })}
              style={tStyles.cmdLaunch}
            >
              cancel
            </button>
            <button
              onClick={run}
              style={{
                ...tStyles.drillButton,
                marginTop: 0,
                color: COLORS.down,
              }}
              title="POST /api/cron/run?lookback_days=30"
            >
              confirm — fetch 30d
            </button>
          </>
        )}
        {state.kind === "running" && (
          <button
            disabled
            style={{
              ...tStyles.cmdLaunch,
              opacity: 0.6,
              cursor: "wait",
            }}
          >
            backfilling… (may take a minute)
          </button>
        )}
        {state.kind === "done" && (
          <button
            onClick={() => setState({ kind: "idle" })}
            style={{ ...tStyles.cmdLaunch, color: COLORS.accent }}
            title={`${state.specs} specs · ${state.calls} calls · ${fmtMillsUsd(state.mills)}`}
          >
            {state.calls === 0
              ? "✓ no gaps — already filled"
              : `✓ ${state.specs} specs · ${state.calls} calls · ${fmtMillsUsd(state.mills)}`}
          </button>
        )}
        {state.kind === "error" && (
          <button
            onClick={() => setState({ kind: "idle" })}
            style={{ ...tStyles.cmdLaunch, color: COLORS.down }}
            title={state.msg}
          >
            ✗ {state.msg}
          </button>
        )}
      </div>
    </div>
  );
}
